import { round } from '@myre/common-primitives';

import { type PipeTransform } from '@angular/core';

import {
  type ContentPosition,
  type Positionable,
  type TextInputType,
} from '../components/textbox/textbox.component';
import {
  amountLimitValidators,
  type AmountErrorFormatter,
} from '../validators/amount.validator';

import {
  QuestionBase,
  type QuestionBaseWithoutType,
  type SetValueOptions,
} from './question-base';

export interface IQuestionFinancialValue
  extends QuestionBaseWithoutType<number>, Positionable {
  tooltip?: string;
  errorFormatter?: AmountErrorFormatter;
  pipe?: PipeTransform;
  onFocus?: () => void;
  onBlur?: () => void;
  displayOnly?: boolean;
  displayOnlyNullValue?: string;
  currency?: string;
  /**
   * Amounts are handled in cents and stored with integer precision.
   * However, an amount factor can require more precision (ex: when dealing with €/m²)
   *
   * Default value is 0 for cents (because of the 100 factor)
   */
  numberOfDigits?: number;
}

export class QuestionFinancialValue
  extends QuestionBase<number>
  implements IQuestionFinancialValue
{
  declare public type?: TextInputType;
  public mask?: string;
  public tooltip: string;
  public errorFormatter?: AmountErrorFormatter;
  public pipe?: PipeTransform;
  public onFocus?: () => void;
  public onBlur?: () => void;
  public displayOnly?: boolean;
  public displayOnlyNullValue?: string;
  public currency?: string;
  public contentPosition: ContentPosition;
  public numberOfDigits: number;

  constructor(options: IQuestionFinancialValue) {
    super(options);
    this.elementType = 'textbox';
    this.type = 'number';

    this.min = options.min;
    this.max = options.max;

    this.displayOnly = options.displayOnly;
    this.displayOnlyNullValue = options.displayOnlyNullValue;

    this.onFocus = options.onFocus || (() => {});
    this.onBlur = options.onBlur || (() => {});

    this.tooltip = options.tooltip || '';
    this.errorFormatter = options.errorFormatter;
    this.currency = options.currency;
    this.pipe = options.pipe;
    this.contentPosition = options.contentPosition || 'before';
    this.numberOfDigits = options.numberOfDigits || 0;

    this._formatValue ??= this._defaultFormatValue;
  }

  public override getUnit(): string {
    if (this.unit) {
      return this.unit;
    }

    if (this.pipe) {
      return this.pipe.transform('');
    }
  }

  public getPipe(): PipeTransform {
    if (this.pipe) {
      return this.pipe;
    }

    return null;
  }

  public override initStart(): void {
    this.validators.push(
      amountLimitValidators(
        this.errorFormatter ||
          this.questionControlService.financialErrorFormatter.bind(
            this.questionControlService,
          ),
        this.pipe,
      ),
    );
  }

  public override setValue(
    value: number,
    opts: SetValueOptions = { emitEvent: true, force: false },
  ): void {
    const formatted = this._formatValue(value);

    if (!this.equals(value) || opts.force) {
      return super.setValue(
        typeof formatted === 'number' ? formatted / 100 : formatted,
        {
          ...opts,
          force: true,
        },
      );
    }
  }

  public override getValue(): number {
    const formatted = this._formatValue(super.getValue());

    return typeof formatted === 'number'
      ? round(formatted * 100, this.numberOfDigits)
      : formatted;
  }

  public override equals(value: any): boolean {
    return this.getValue() === this._formatValue(value);
  }

  /**
   * @override
   */
  public override getUnitTag(): string | null {
    const unit = this.getUnit();

    if (unit && this.currency) {
      return `${this.currency}/${unit}`;
    }

    if (this.currency) {
      return this.currency;
    }

    return super.getUnitTag();
  }

  private _defaultFormatValue(value: number): number {
    if (value === undefined) {
      return;
    }

    if (String(value).trim() === '' || value === null) {
      return null;
    }

    return +String(value).trim();
  }
}
